import {
  ListingFragment,
  MlsListingAddressObject,
  MlsListingFragment,
  MlsListingStatus,
  PropertyDetailsFragment,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';

export interface NormalizedPropertyDetails {
  id?: string;
  mlsListingId?: string;
  photos?: string[];
  listPrice?: number;
  streetAddress?: string;
  secondAddressLine?: string;
  bedrooms?: number;
  bathrooms?: string | number;
  area?: number;
  daysOnMarket?: number;
  location?: {
    lat: number;
    lon: number;
  };
}

export const getPropertyDetails = (
  listing?: ListingFragment | PublicListingFragment,
  propertyDetails?: PropertyDetailsFragment
): NormalizedPropertyDetails => {
  if (!listing && !propertyDetails) {
    return {};
  }
  const id = listing?.id || propertyDetails?.id;
  const mlsListingId = listing?.mlsListing?.id;
  const photos = listing?.mlsListing?.photos;
  const listPrice =
    listing?.mlsListing?.listPrice || propertyDetails?.targetPrice;
  const streetAddress = listing
    ? `${listing?.mlsListing?.address?.streetNumberText} ${listing?.mlsListing
        ?.address?.streetName} ${listing?.mlsListing?.address?.unit ?? ''}`
    : `${propertyDetails?.streetAddress} ${propertyDetails?.unit ?? ''}`;
  const secondAddressLine = listing
    ? `${listing?.mlsListing?.address?.city}, ${listing?.mlsListing?.address?.state} ${listing?.mlsListing?.address?.postalCode}`
    : `${propertyDetails?.city}, ${propertyDetails?.state} ${propertyDetails?.zipCode}`;
  const bedrooms =
    listing?.mlsListing?.property?.bedrooms || propertyDetails?.bedrooms;
  const bathrooms =
    listing?.mlsListing?.property?.bathrooms ||
    (propertyDetails?.bathsFull || 0) + (propertyDetails?.bathsHalf || 0);
  const area = listing?.mlsListing?.property?.area || propertyDetails?.areaSqFt;
  const daysOnMarket = listing?.mlsListing?.listDate
    ? dayjs().diff(dayjs(listing?.mlsListing?.listDate), 'days')
    : undefined;
  const location =
    listing?.mlsListing?.geo?.location ||
    (propertyDetails?.geo && {
      lat: propertyDetails?.geo?.lat,
      lon: propertyDetails?.geo?.lng,
    });

  return {
    id,
    mlsListingId,
    photos,
    listPrice,
    streetAddress,
    secondAddressLine,
    bedrooms,
    bathrooms,
    area,
    daysOnMarket,
    location,
  };
};

// Transforms an off market PropertyDetailsFragment into MlsListingFragment structure.
// This is a hack to allow PropertyDetails to be used with the ComparableColumn components.
export const transformPropertyDetailsToMlsListing = (
  propertyDetails?: PropertyDetailsFragment
): Partial<MlsListingFragment> | undefined => {
  if (!propertyDetails) {
    return {};
  }

  const mlsListingId = propertyDetails?.id;
  const listPrice = propertyDetails?.targetPrice;
  const fullAddress = `${propertyDetails?.streetAddress} ${
    propertyDetails?.unit ?? ''
  } ${propertyDetails?.city}, ${propertyDetails?.state} ${propertyDetails?.zipCode}`;
  const bedrooms = propertyDetails?.bedrooms;
  const bathrooms =
    (propertyDetails?.bathsFull || 0) + (propertyDetails?.bathsHalf || 0);
  const area = propertyDetails?.areaSqFt;
  const location = propertyDetails?.geo && {
    lat: propertyDetails?.geo?.lat,
    lon: propertyDetails?.geo?.lng,
  };

  return {
    id: mlsListingId,
    photos: [],
    listPrice,
    address: {
      full: fullAddress,
      city: propertyDetails?.city,
      state: propertyDetails?.state,
      postalCode: propertyDetails?.zipCode,
      streetName: propertyDetails?.streetAddress,
      streetNumberText: propertyDetails?.streetAddress,
    } as MlsListingAddressObject,
    property: {
      bedrooms,
      bathsFull: propertyDetails?.bathsFull,
      bathsHalf: propertyDetails?.bathsHalf,
      bathrooms: bathrooms.toString(),
      area,
    },
    geo: {
      location,
    },
    mls: {
      status: 'Off Market' as unknown as MlsListingStatus,
      statusText: 'Off Market',
    },
  };
};
